import type { TOC } from '@ember/component/template-only';
import { PrismLoadingBar, PrismViewSwitcher } from '@mylighthouse/prism-ember';
import type { Dayjs } from 'dayjs';
import { t } from 'ember-intl';
import { pageTitle } from 'ember-page-title';
import { eq } from 'ember-truth-helpers';
import type { SupportedCurrencies } from 'frontend-utils/utils/constants/currency-mapping-constants';
import GeneralDatepickersDatepickerWithSidebar, {
  type GeneralDatepickersDatepickerWithSidebarSignature,
} from 'lumen/components/general/datepickers/datepicker-with-sidebar';
import GeneralDropdownsDropdownPageSize from 'lumen/components/general/dropdowns/dropdown-page-size';
import GeneralPageHeader from 'lumen/components/general/page/header';
import GeneralPageMain from 'lumen/components/general/page/main';
import GeneralPerformancePageTitle from 'lumen/components/general/performance/page-title';
import GeneralTablesPaginationFooter from 'lumen/components/general/tables/pagination-footer';
import MarketSnapshotsCard from 'lumen/components/market/snapshots/card';
import MarketSnapshotsKpiSection from 'lumen/components/market/snapshots/kpi-section';
import MarketSnapshotsTable, {
  type MarketSnapshotsTableSignature,
} from 'lumen/components/market/snapshots/table';
import MarketSnapshotsViewSwitcher from 'lumen/components/market/snapshots/view-switcher';
import contextualPageTitle from 'lumen/helpers/contextual-page-title';
import type MarketPerformanceDataItem from 'lumen/models/market-performance/data-item';
import type { MarketPerformanceTotals } from 'lumen/models/market-performance/data-item';
import type MarketPerformanceKpi from 'lumen/models/market-performance/kpi';
import {
  FIRST_HEADER_HEIGHT,
  SUB_HEADER_HEIGHT,
} from 'lumen/utils/constants/general-constants';
import {
  type ComparisonMetricType,
  GROUP_BY,
  type GroupBy,
  type SnapshotView,
} from 'lumen/utils/constants/market-snapshots-constants';
import type { KpiCardName } from 'lumen/utils/market-snapshots/kpis';

type DatepickerArgs = GeneralDatepickersDatepickerWithSidebarSignature['Args'];
type TableArgs = MarketSnapshotsTableSignature['Args'];
type PageSizeOption = { label: string; value: number };

export interface MarketSnapshotsPageDates {
  fromDate: string | undefined;
  maxDate: Dayjs;
  onChange: (range: { end: Dayjs | null; start: Dayjs }) => void;
  range: { end: Dayjs; start: Dayjs };
  sidebarLists: DatepickerArgs['sidebarLists'];
  toDate: string | undefined;
  // Set only in month view.
  triggerLabelFormatter: DatepickerArgs['triggerLabelFormatter'] | undefined;
}

export interface MarketSnapshotsPageKpiSection {
  comparisonMetricType: ComparisonMetricType;
  currency: SupportedCurrencies;
  isLoading: boolean;
  kpis: MarketPerformanceKpi[];
  names: readonly KpiCardName[];
}

export interface MarketSnapshotsPagePagination {
  lastPage: number;
  onChangePageSize: (pageSizeOption: PageSizeOption) => void;
  onSetPage: (page: number) => void;
  page: number;
  pageSizeOptions: number[];
  pageSizeValue: PageSizeOption;
  showFooter: boolean;
  showingText: string;
  totalRecords: number | null;
}

export interface MarketSnapshotsPageTable {
  groupBy: GroupBy;
  groupColumnsBy: TableArgs['groupColumnsBy'];
  isLoading: boolean;
  isPaginated: boolean;
  isSortable: boolean;
  items: MarketPerformanceDataItem[];
  kpisFor: TableArgs['kpisFor'];
  onSetGroupBy: (groupBy: string) => void;
  onSort: TableArgs['onSort'];
  orderBy: string;
  totals: MarketPerformanceTotals | undefined;
  varianceType: TableArgs['varianceType'];
  visibleKpis: TableArgs['visibleKpis'];
  visibleMetricTypes: TableArgs['visibleMetricTypes'];
}

export interface MarketSnapshotsPageSignature {
  Args: {
    dates: MarketSnapshotsPageDates;
    kpiSection: MarketSnapshotsPageKpiSection;
    pagination: MarketSnapshotsPagePagination;
    table: MarketSnapshotsPageTable;
    title: string;
    view: SnapshotView;
  };
  Blocks: {
    // Next to the title.
    controls: [];
    // Next to the table heading.
    tableControls: [];
  };
}

const LOADING_BAR_POSITION_TOP = FIRST_HEADER_HEIGHT + SUB_HEADER_HEIGHT;

const MarketSnapshotsPage: TOC<MarketSnapshotsPageSignature> = <template>
  {{pageTitle (contextualPageTitle @title)}}

  <GeneralPageHeader @isSmall={{true}}>
    <div class="content-wrapper flex items-center gap-200">
      <div
        class="flex items-center divide-x divide-[var(--prism-color-border-neutral-default)] *:not-first:ps-200 *:not-last:pe-200"
      >
        <MarketSnapshotsViewSwitcher
          @fromDate={{@dates.fromDate}}
          @groupBy={{@table.groupBy}}
          @toDate={{@dates.toDate}}
          @view={{@view}}
        />

        {{! @glint-expect-error: Incorrect type }}
        <GeneralDatepickersDatepickerWithSidebar
          @maxDate={{@dates.maxDate}}
          @maxRange="2 years"
          @range={{@dates.range}}
          @rangeChanged={{@dates.onChange}}
          @sidebarLists={{@dates.sidebarLists}}
          @triggerLabelFormatter={{@dates.triggerLabelFormatter}}
        />
      </div>

      <div class="ms-auto flex items-center gap-200">
        <PrismViewSwitcher
          @activeButtonId={{@table.groupBy}}
          @onSetActiveButtonId={{@table.onSetGroupBy}}
          as |ViewSwitcher|
        >
          <ViewSwitcher.button
            @id={{GROUP_BY.PROPERTY}}
            @label={{t "market.snapshots.group_by.properties"}}
            data-test-id="group-by-property"
          />

          <ViewSwitcher.button
            @id={{GROUP_BY.MONTH}}
            @label={{t "market.snapshots.group_by.months"}}
            data-test-id="group-by-month"
          />
        </PrismViewSwitcher>
      </div>
    </div>
  </GeneralPageHeader>

  {{#if @table.isLoading}}
    <PrismLoadingBar
      @isFixed={{true}}
      @positionTop={{LOADING_BAR_POSITION_TOP}}
    />
  {{/if}}

  <GeneralPageMain>
    <div class="content-wrapper main-content flex h-full flex-col gap-600">
      <GeneralPerformancePageTitle
        @count={{unless
          (eq @table.groupBy GROUP_BY.MONTH)
          @pagination.totalRecords
        }}
        @dataTestId="snapshots-count"
        @title={{@title}}
      >
        {{yield to="controls"}}
      </GeneralPerformancePageTitle>

      <MarketSnapshotsKpiSection
        @comparisonMetricType={{@kpiSection.comparisonMetricType}}
        @currency={{@kpiSection.currency}}
        @isLoading={{@kpiSection.isLoading}}
        @kpiNames={{@kpiSection.names}}
        @kpis={{@kpiSection.kpis}}
      />

      <div class="flex items-center justify-between">
        <h3 class="text-neutral-emphasis" data-test-id="data-section-title">
          {{t "general.breakdown"}}
        </h3>

        {{yield to="tableControls"}}
      </div>

      <MarketSnapshotsCard @showFooter={{@pagination.showFooter}}>
        <:content>
          <MarketSnapshotsTable
            @dataItems={{@table.items}}
            @dateRange={{@dates.range}}
            @groupBy={{@table.groupBy}}
            @groupColumnsBy={{@table.groupColumnsBy}}
            @isLoading={{@table.isLoading}}
            @isPaginated={{@table.isPaginated}}
            @isSortable={{@table.isSortable}}
            @kpisFor={{@table.kpisFor}}
            @onSort={{@table.onSort}}
            @orderBy={{@table.orderBy}}
            @totals={{@table.totals}}
            @varianceType={{@table.varianceType}}
            @visibleKpis={{@table.visibleKpis}}
            @visibleMetricTypes={{@table.visibleMetricTypes}}
          />
        </:content>

        <:footer>
          <GeneralTablesPaginationFooter
            @currentPage={{@pagination.page}}
            @lastPage={{@pagination.lastPage}}
            @onChangePageSize={{@pagination.onChangePageSize}}
            @onSetPage={{@pagination.onSetPage}}
            @pageSizeDropdown={{GeneralDropdownsDropdownPageSize}}
            @pageSizeOptions={{@pagination.pageSizeOptions}}
            @pageSizeValue={{@pagination.pageSizeValue}}
            @showingText={{@pagination.showingText}}
          />
        </:footer>
      </MarketSnapshotsCard>
    </div>
  </GeneralPageMain>
</template>;

export default MarketSnapshotsPage;
